import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, Download, Printer } from 'lucide-react';
import { DataStore, Customer } from '@/lib/data-store';
import { CustomerForm } from '@/components/CustomerForm';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { generateCustomerCode, getNextCustomerNumber, getInitials, getInitialsColor } from '@/lib/customer-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const Pelanggan = () => {
  const [customers, setCustomers] = useState<Customer[]>(DataStore.getData().customers);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: Customer) => {
    const newCustomers = editingId
      ? customers.map(c => c.id === editingId ? { ...data, id: editingId } : c)
      : [...customers, data];
    
    setCustomers(newCustomers);
    DataStore.saveData({ ...DataStore.getData(), customers: newCustomers });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = customers.filter(c => c.id !== id);
    setCustomers(filtered);
    DataStore.saveData({ ...DataStore.getData(), customers: filtered });
  };

  const filteredCustomers = customers.filter(c =>
    c.namaSyarikat.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.kodPelanggan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nextCustomerCode = generateCustomerCode(getNextCustomerNumber(customers));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">1. PELANGGAN</h1>
          <p className="text-slate-600 mt-1">SEBUTHARGA : INVOIS</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {customers.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(customers, [
                    { label: 'Kod', key: 'kodPelanggan' },
                    { label: 'Nama Syarikat', key: 'namaSyarikat' },
                    { label: 'Email', key: 'email' },
                    { label: 'Telepon', key: 'telp' },
                    { label: 'Alamat', key: 'alamat' },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR PELANGGAN', tableHTML, 'DATA PELANGGAN');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(customers, 'Data Pelanggan')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Pelanggan Baru</span>
          </Button>
        </div>
      </div>

      {/* Tabs & Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button size="sm" className="bg-red-600 hover:bg-red-700">Pelanggan</Button>
              <Button size="sm" variant="outline">Sebutharga</Button>
              <Button size="sm" variant="outline">Invois</Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <Input 
                placeholder="Cari pelanggan..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer List View */}
      <div id="customers-list" className="space-y-3">
        {filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-slate-500">
                <p className="text-lg font-medium mb-2">Tiada pelanggan</p>
                <p className="text-sm">Klik tombol "Baru" untuk menambahkan pelanggan</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Avatar & Basic Info */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center font-bold text-white flex-shrink-0 ${getInitialsColor(getInitials(customer.namaSyarikat))}`}>
                      {getInitials(customer.namaSyarikat)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 mb-1">
                        <h3 className="text-lg font-bold text-slate-900">{customer.namaSyarikat}</h3>
                        <p className="text-xs text-red-600 font-medium">{customer.kodPelanggan}</p>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{customer.alamat}</p>
                      <div className="flex gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="text-slate-700">{customer.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Telepon</p>
                          <p className="text-slate-700">{customer.telp}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Status & Actions */}
                  <div className="flex flex-col items-end gap-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      customer.status === 'Aktif' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {customer.status}
                    </span>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleEdit(customer.id)} 
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Sunting"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        onClick={() => handleDelete(customer.id)} 
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Padam"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Customer Form Modal */}
      {showForm && (
        <CustomerForm
          customer={editingId ? customers.find(c => c.id === editingId) : undefined}
          kodPelanggan={editingId ? customers.find(c => c.id === editingId)?.kodPelanggan || nextCustomerCode : nextCustomerCode}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default Pelanggan;